import type React from 'react';
import { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useIntl } from 'react-intl';
import { useAppState } from 'state';

import { trackEvent } from 'src/app/utils/analytics/tracker';
import { PlanTrackingLocation } from 'src/app/utils/analytics/types';

import GenericPage from 'app/ui/components/layouts/generic-page';
import ErrorComponent from 'app/ui/components/molecules/error';
import Loading from 'app/ui/components/molecules/loading';
import { AccordionItem, AccordionContent } from 'app/ui/components/molecules/accordion';
import { PlusIcon, MinusIcon } from 'app/ui/components/atoms/plus-minus';
import WorkoutRail from 'app/ui/components/molecules/workout-rail';
import { lessonToWorkout } from 'app/utils/workouts';
import { routes } from 'app/routes/constants';
import Button from 'app/ui/components/atoms/button';
import PopupDialog from 'app/ui/components/molecules/popup-dialog';
import MoreTrainingPlans from 'app/ui/components/molecules/more-training-plans';

import {
  RestDayWorkout,
  StyledHeader,
  BreadcrumbWrapper,
  StyledBreadcrumb,
  StyledButton,
  VideosContainer,
  AccordionContainer,
  TitleWithContent,
  EndContentWithIcons,
  Icons,
  StyledAccordionTitle,
  StyledTypography,
} from 'app/pages/progressive-plan/components';

import { ProgressivePlan as PROGRESSIVE_PLAN } from 'app/pages/progressive-plan/query.gql';
import type { ProgressivePlanQuery, ProgressivePlanQueryVariables } from 'app/types/graphql';

import VideoPlayer from 'app/ui/components/molecules/video-component';
import Divider from 'src/app/ui/components/atoms/divider';
import ResourceSections from 'app/ui/components/molecules/resource-sections';
import Typography from 'src/app/ui/components/atoms/typography';

import Tooltip from 'app/ui/components/molecules/tooltip';
import { TitleWithTooltip } from 'app/ui/components/molecules/tooltip/util';
import QuestionMarkCircle from 'app/ui/components/atoms/icons/question-mark-circle';
import { PlansCarouselModal } from 'app/ui/components/molecules/carousel-modal';
import { useStartProgressivePlan } from 'app/hooks/use-start-progressive-plan';

const StyledResourceSections = styled(ResourceSections)`
  margin-bottom: ${({ theme }) => theme.spacing(18)};
`;

const BlockIntroText = styled(Typography)`
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const ProgressivePlanPreview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const intl = useIntl();
  const theme = useTheme();
  const userId = useAppState(({ auth }) => auth.userId) as number;

  const [userOpenBlockId, setUserOpenBlockId] = useState<string | null>(null);
  const [blockDialogConfig, setBlockDialogConfig] = useState<{
    primaryText: string;
    primaryButton?: React.ReactNode;
  } | null>(null);

  const { data, loading, error } = useQuery<ProgressivePlanQuery, ProgressivePlanQueryVariables>(PROGRESSIVE_PLAN, {
    variables: { id, userId },
    skip: !id || !userId,
  });

  const planTitle = data?.progressivePlanById?.name || '';

  const {
    handleStartPlanClick: handleStartPlan,
    handleStartPlanConfirm,
    isCarouselModalOpen,
    setIsCarouselModalOpen,
    dialogConfig: startPlanDialogConfig,
    error: startPlanError,
  } = useStartProgressivePlan({
    planId: id || '',
    planTitle,
    trackingEventName: 'start_plan_cta_clicked',
  });

  useEffect(() => {
    if (data?.progressivePlanById?.blocks?.length) {
      setUserOpenBlockId(data.progressivePlanById.blocks[0].id);
    }
  }, [data?.progressivePlanById?.blocks]);

  if (loading) {
    return <Loading />;
  }
  if (error || !data?.progressivePlanById || startPlanError) {
    return <ErrorComponent />;
  }

  const plan = data.progressivePlanById;
  const blocks = plan.blocks || [];
  const initialBlockId = blocks[0]?.id ?? null;
  const openBlockId = userOpenBlockId === undefined ? initialBlockId : userOpenBlockId;

  const hasActiveInstance =
    data.userById?.gym?.activeProgressivePlans?.some((instance) => instance?.progressivePlan?.id === id) ?? false;

  const handleStartPlanClick = () => {
    if (!hasActiveInstance) {
      handleStartPlan();
    }
  };

  const startPlanButton = (
    <StyledButton
      label={intl.formatMessage({ id: 'progressivePlan.startPlan', defaultMessage: 'Start plan' })}
      onClick={handleStartPlanClick}
      state={hasActiveInstance ? 'disabled' : 'default'}
    />
  );

  const handleBlockClick = (blockId: string, index: number) => {
    if (index === 0) {
      // Toggle first block
      setUserOpenBlockId(openBlockId === blockId ? null : blockId);
    } else {
      setBlockDialogConfig({
        primaryText: intl.formatMessage({
          id: 'dialog.startPlanAlert',
          defaultMessage: 'You need to start the plan to unlock this block',
        }),
        primaryButton: (
          <Button
            label={intl.formatMessage({ id: 'dialog.close', defaultMessage: 'Close' })}
            onClick={() => setBlockDialogConfig(null)}
          />
        ),
      });
    }
  };

  return (
    <GenericPage pageTitle={plan.name}>
      <StyledHeader
        title={plan.name}
        content={plan.longDescription || ''}
        imageUrl={plan.bannerImage?.signedUrl || plan.bannerImage?.url || ''}
        gradient={theme.colors.gold}
      />
      <BreadcrumbWrapper>
        <StyledBreadcrumb contentTitle={plan.name} />
        {hasActiveInstance ? (
          <Tooltip
            tooltipText={intl.formatMessage({
              id: 'progressivePlan.alreadyActive',
              defaultMessage: 'An active plan instance for this plan already exists',
            })}
          >
            {startPlanButton}
          </Tooltip>
        ) : (
          startPlanButton
        )}
      </BreadcrumbWrapper>

      <VideosContainer>
        {plan.overviewVideo && (plan.overviewVideo.url || plan.overviewVideo.signedUrl) && (
          <VideoPlayer
            src={plan.overviewVideo.signedUrl || plan.overviewVideo.url || ''}
            title={intl.formatMessage({ id: 'progressivePlan.overview', defaultMessage: 'Overview' })}
            thumbnail={plan.overviewVideo.thumbnail?.signedUrl || plan.overviewVideo.thumbnail?.url}
            trackEventOnClickPlay={() =>
              trackEvent('plan_video_played', {
                plan_id: plan.id,
                plan_title: plan.name,
                video_title: intl.formatMessage({ id: 'progressivePlan.overview', defaultMessage: 'Overview' }),
                location: PlanTrackingLocation.PLAN_PREVIEW,
              })
            }
          />
        )}
        {blocks.length > 0 && blocks[0].introVideo && (blocks[0].introVideo.url || blocks[0].introVideo.signedUrl) && (
          <VideoPlayer
            src={blocks[0].introVideo.signedUrl || blocks[0].introVideo.url || ''}
            title={blocks[0].name}
            thumbnail={blocks[0].introVideo.thumbnail?.signedUrl || blocks[0].introVideo.thumbnail?.url}
            trackEventOnClickPlay={() =>
              trackEvent('plan_video_played', {
                plan_id: plan.id,
                plan_title: plan.name,
                plan_block_id: blocks[0].id,
                video_title: blocks[0].name,
                location: PlanTrackingLocation.PLAN_PREVIEW,
              })
            }
          />
        )}
      </VideosContainer>

      <AccordionContainer>
        {blocks.map((block, index) => {
          const isOpen = openBlockId === block.id;
          const isDisabled = index !== 0;

          return (
            <AccordionItem key={block.id}>
              <StyledAccordionTitle
                $isOpen={isOpen}
                $isDisabled={isDisabled}
                onClick={() => handleBlockClick(block.id, index)}
              >
                <TitleWithContent $isOpen={isOpen}>
                  <TitleWithTooltip>
                    <StyledTypography variant="h6">{`${index + 1}. ${block.name}`}</StyledTypography>
                    {index === 0 && (
                      <Tooltip tooltipText={intl.formatMessage({ id: 'trainingPlan.tooltip.phase' })} usePortal={true}>
                        <QuestionMarkCircle color={isOpen ? theme.colors.focoltone : undefined} />
                      </Tooltip>
                    )}
                  </TitleWithTooltip>
                </TitleWithContent>
                <EndContentWithIcons>
                  <Icons>
                    {isOpen ? <MinusIcon color={theme.colors.focoltone} /> : <PlusIcon color={theme.colors.white} />}
                  </Icons>
                </EndContentWithIcons>
              </StyledAccordionTitle>
              <AccordionContent $isOpen={isOpen}>
                {block.introText && (
                  <BlockIntroText variant="caption" color={theme.colors.lightGray}>
                    {block.introText}
                  </BlockIntroText>
                )}
                {block.weeks?.map((week) => (
                  <WorkoutRail
                    key={week.id}
                    title={<StyledTypography variant="h6">{week.title || `Week ${week.weekNumber}`}</StyledTypography>}
                    description={week.shortDescription}
                    workouts={(week.workouts || []).map(({ lesson, challenge }) => {
                      if (lesson) {
                        return lessonToWorkout(lesson, {});
                      }
                      if (challenge?.lesson) {
                        return lessonToWorkout(challenge.lesson, {
                          link: routes.CHALLENGE.replace(':id', challenge.id),
                        });
                      }
                      return RestDayWorkout;
                    })}
                    showProgress={false}
                    cardVariant="simplified"
                  />
                ))}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </AccordionContainer>

      {plan.resource?.sections && plan.resource.sections.length > 0 && (
        <>
          <Divider variant="sm" fullWidth={true} />
          <StyledResourceSections sections={plan.resource.sections} resourceId={plan.resource.id} />
        </>
      )}

      <Divider variant="sm" fullWidth={true} />

      <MoreTrainingPlans currentPlanId={id} />

      <PopupDialog
        isOpen={!!startPlanDialogConfig || !!blockDialogConfig}
        primaryText={startPlanDialogConfig?.primaryText || blockDialogConfig?.primaryText || ''}
        primaryButton={startPlanDialogConfig?.primaryButton || blockDialogConfig?.primaryButton}
      />

      <PlansCarouselModal
        isOpen={isCarouselModalOpen}
        onOpenChange={setIsCarouselModalOpen}
        onStartPlan={handleStartPlanConfirm}
      />
    </GenericPage>
  );
};

export default ProgressivePlanPreview;
